import { useMemo } from "react";
import { Download } from "lucide-react";
import type { ViewPrimitive, BladeContext } from "../../shell/registry";
import { BladeLoading, BladeError, BladeDenied, BladeEmpty, forcedBladeState } from "../../shell/BladeStates";
import { useCodeCases } from "../AuditDataSource";
import { reasonLabel } from "../data/reasons";
import { codeCasesCsv, codeCasesFilename, downloadCsv } from "../csv";
import type { CodeCaseQuery, CodeCaseRow } from "../types";
import { fieldLabel } from "./codeTableToolbar";

// The cases behind one report row (HUB-810 Task 8). Opened as a child of the
// metrics blade, so the breadcrumb records where the reader came from, and
// keyed by its query params so a refresh or a shared link reopens the same
// list.
//
// AUDITOR-ONLY on the server. A reviewer who reaches this (a shared link, say)
// gets the denied state, never an empty table that reads as "no such cases".

export function queryFromParams(params: Record<string, string> | undefined): CodeCaseQuery | null {
  const mode = params?.mode;
  if (mode !== "corpus" && mode !== "errored") return null;
  const q: CodeCaseQuery = { mode };
  if (params?.field) q.field = params.field;
  if (params?.code) q.code = params.code;
  if (params?.reason) q.reason = params.reason;
  return q;
}

const when = (iso: string) => (iso ? new Date(iso).toLocaleDateString() : "—");

function CodeCasesBody({ ctx }: { ctx: BladeContext }) {
  const q = useMemo(() => queryFromParams(ctx.descriptor.params), [ctx.descriptor.params]);
  const { status, data } = useCodeCases(q ?? undefined);
  if (!q) return <BladeError message="This link does not name a code or a reason." />;
  if (status === "loading") return <BladeLoading />;
  if (status === "denied") return <BladeDenied description="Only auditors can open the cases behind a code. Ask an auditor for this list." />;
  if (status === "error" || !data) return <BladeError message="Something went wrong loading these cases." />;
  if (data.rows.length === 0) return <BladeEmpty title="No cases" description="Nothing matches this code or reason under the current study key." />;

  const errored = q.mode === "errored";
  const open = (row: CodeCaseRow) => {
    ctx.openChild({ key: "audit-review", entityId: row.id, title: row.id, width: "xwide" });
    ctx.setMode("focus");
  };
  const exportCsv = () => downloadCsv(codeCasesFilename(q), codeCasesCsv(data.rows, q.mode));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {data.total.toLocaleString()} {data.total === 1 ? "case" : "cases"}
          {q.code ? <> carrying <span className="font-medium tabular-nums text-foreground">{q.code}</span> in {fieldLabel(q.field ?? "")}</> : null}
          {q.reason ? <> cited for <span className="font-medium text-foreground">{reasonLabel(q.reason)}</span></> : null}
          {errored ? " · judged wrong" : " · either side"}
        </p>
        <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-coronis border border-border bg-card px-3 py-1.5 text-[12.5px] font-medium text-foreground hover:bg-muted">
          <Download className="size-3.5" aria-hidden /> Export CSV
        </button>
      </div>
      <p className="text-[11.5px] leading-snug text-muted-foreground">
        The export carries document and episode ids, codes, dates, verdicts and reason ids — no patient details, notes or reviewer names. Treat the file as PHI.
      </p>
      <div className="overflow-auto rounded-coronis border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
              <th className="py-2 pl-4 pr-3 font-medium">Case</th>
              <th className="px-3 py-2 font-medium">Batch</th>
              <th className="px-3 py-2 font-medium">Received</th>
              {errored ? (
                <>
                  <th className="px-3 py-2 font-medium">Reviewed</th>
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 font-medium">Wrong side</th>
                  <th className="px-3 py-2 font-medium">Verdict</th>
                  <th className="px-3 py-2 pr-4 font-medium">Reasons</th>
                </>
              ) : (
                <>
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 pr-4 font-medium">Both sides</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr
                key={`${r.id}:${r.field}:${r.code}:${r.side}:${i}`}
                onClick={(e) => {
                  // Stopped so the click does not also bubble to the blade's
                  // own activate-on-click wrapper, which would dispatch
                  // 'activate' at THIS blade's index right after 'open'
                  // pushed the child — reverting the navigation in the same
                  // event.
                  e.stopPropagation();
                  open(r);
                }}
                className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50"
              >
                <td className="py-2.5 pl-4 pr-3 font-medium tabular-nums text-foreground">{r.episodeId || r.id}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.batchId}</td>
                <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{when(r.receivedAt)}</td>
                {errored ? (
                  <>
                    <td className="px-3 py-2.5 tabular-nums text-muted-foreground">{when(r.reviewSubmittedAt)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{fieldLabel(r.field)}</td>
                    <td className="px-3 py-2.5 capitalize text-foreground">{r.side || "—"}</td>
                    <td className="px-3 py-2.5 capitalize text-foreground">{r.verdict || "—"}</td>
                    <td className="px-3 py-2.5 pr-4 text-foreground">{r.reasons.map(reasonLabel).join("; ") || "—"}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2.5 text-muted-foreground">{fieldLabel(r.field)}</td>
                    <td className="px-3 py-2.5 pr-4 text-foreground">{r.matched ? "Yes" : "One side only"}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const auditCodeCasesPrimitive: ViewPrimitive = {
  key: "audit-code-cases",
  defaultTitle: "Cases",
  defaultEyebrow: "Program metrics",
  defaultWidth: "xwide",
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === "loading") return <BladeLoading />;
    if (forced === "error") return <BladeError />;
    if (forced === "denied") return <BladeDenied />;
    if (forced === "empty") return <BladeEmpty title="No cases" description="Nothing matches this code or reason." />;
    return <CodeCasesBody ctx={ctx} />;
  },
};
